// pages/UpgradeProfile.js
//
// Subscription page:
//   - new user        -> pick Monthly or Yearly and start the 1-month free trial.
//                        Card is taken by Stripe, $0 today, auto-charged when
//                        the trial ends, then renews every month / year.
//   - on free trial   -> days left, when the first charge happens, Manage billing (cancel)
//   - subscribed      -> current plan + Manage billing
//   - trial/plan over -> must choose Monthly or Yearly

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../common/LoadingSpinner';
import config from '../../config';
import { toast } from '../../components/common/Toast';
import { updateUserInfo } from '../../features/user/userSlice';

const AFTER_ACCESS_PATH = '/';
const SUBSCRIPTION_PATH = '/upgrade-profile';

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString() : '';

const errorMessage = (err, fallback) =>
  err?.response?.data?.message || fallback;

const styles = {
  banner: {
    background: '#EEF6FF',
    border: '1px solid #C9DEEE',
    borderRadius: 12,
    padding: '16px 20px',
    marginBottom: 24,
  },

  trialCard: {
    background:
      'linear-gradient(135deg, #0F4C75 0%, #1B6CA8 100%)',
    color: 'white',
    borderRadius: 16,
    padding: '28px 24px',
    marginBottom: 28,
    textAlign: 'center',
  },

  grid: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 20,
  },

  card: (highlight) => ({
    position: 'relative',
    background: 'white',
    borderRadius: 16,
    padding: 24,
    border: highlight
      ? '2px solid #0F4C75'
      : '1px solid #E3E6EA',
    boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
    display: 'flex',
    flexDirection: 'column',
  }),

  badge: {
    position: 'absolute',
    top: -12,
    right: 16,
    background: '#16A34A',
    color: 'white',
    borderRadius: 999,
    padding: '4px 12px',
    fontSize: 12,
    fontWeight: 700,
  },

  price: {
    fontSize: 36,
    fontWeight: 800,
    margin: '8px 0 0',
  },

  button: (disabled) => ({
    marginTop: 'auto',
    width: '100%',
    padding: '12px 16px',
    borderRadius: 10,
    border: 'none',
    fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: disabled ? '#E3E6EA' : '#0F4C75',
    color: disabled ? '#6B7280' : 'white',
  }),

  linkButton: {
    background: 'none',
    border: '1px solid #0F4C75',
    color: '#0F4C75',
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

const UpgradeProfile = () => {
  const { token } = useSelector((state) => state.user);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [catalog, setCatalog] = useState({
    trial: null,
    plans: [],
  });

  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const client = useMemo(
    () =>
      axios.create({
        baseURL: `${config.baseURL}/api/subscription`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    [token]
  );

  // Keep stored user information in sync
  const syncUser = useCallback(
    (a) => {
      if (!a) return;

      dispatch(
        updateUserInfo({
          has_access: a.has_access,
          subscription_state: a.state,
          plan_status: a.subscription?.status || null,
          plan_name: a.subscription?.plan_name || null,
          start_date: a.subscription?.started_at || null,
          end_date: a.subscription?.expires_at || null,
          plan_price: a.subscription?.price ?? null,
        })
      );
    },
    [dispatch]
  );

  const load = useCallback(async () => {
    const [plansRes, accessRes] = await Promise.all([
      client.get('/plans/available'),
      client.get('/access'),
    ]);

    setCatalog(
      plansRes.data.data || {
        trial: null,
        plans: [],
      }
    );

    setAccess(accessRes.data.data);

    syncUser(accessRes.data.data);

    return accessRes.data.data;
  }, [client, syncUser]);

  const goToApp = useCallback(() => {
    const from = location.state?.from;

    const target =
      from && !from.startsWith(SUBSCRIPTION_PATH)
        ? from
        : AFTER_ACCESS_PATH;

    navigate(target, {
      replace: true,
    });
  }, [location.state, navigate]);

  // First load + Stripe return
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const params = new URLSearchParams(location.search);
      const sessionId = params.get('session_id');

      if (sessionId) {
        setNotice('Confirming your payment…');

        try {
          const res = await client.post(
            '/stripe/confirm-session',
            {
              sessionId,
            }
          );

          if (!cancelled) {
            syncUser(res.data.data);

            const a = res.data.data;

            toast.success(
              a?.state === 'trial'
                ? 'Your free trial has started — you won’t be charged until it ends.'
                : 'Payment successful — your subscription is active!'
            );
          }
        } catch (err) {
          if (!cancelled) {
            setError(
              errorMessage(
                err,
                'We could not confirm your payment yet. If you were charged, it will show here within a minute — refresh the page.'
              )
            );
          }
        } finally {
          if (!cancelled) {
            setNotice(null);

            navigate(location.pathname, {
              replace: true,
              state: location.state,
            });
          }
        }
      } else if (params.get('checkout') === 'cancelled') {
        setNotice(
          'Checkout was cancelled — you have not been charged.'
        );

        navigate(location.pathname, {
          replace: true,
          state: location.state,
        });
      }

      try {
        await load();
      } catch (err) {
        if (!cancelled) {
          setError(
            errorMessage(
              err,
              'Could not load subscription plans.'
            )
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Free trial
  async function handleStartTrial(plan) {
    setBusy(plan.id);
    setError(null);

    try {
      const res = await client.post('/trial/start', {
        planId: plan.id,
      });

      window.location.href = res.data.data.url;
    } catch (err) {
      setError(
        errorMessage(
          err,
          'Could not start your free trial.'
        )
      );

      setBusy(null);

      await load().catch(() => {});
    }
  }

  // Paid checkout
  async function handleChoosePlan(plan) {
    setBusy(plan.id);
    setError(null);

    try {
      const res = await client.post(
        '/stripe/create-checkout-session',
        {
          planId: plan.id,
        }
      );

      window.location.href = res.data.data.url;
    } catch (err) {
      setError(
        errorMessage(
          err,
          'Could not start checkout.'
        )
      );

      setBusy(null);
    }
  }

  // Stripe billing portal
  async function handleManageBilling() {
    setBusy('portal');
    setError(null);

    try {
      const res = await client.post(
        '/stripe/create-portal-session'
      );

      window.location.href = res.data.data.url;
    } catch (err) {
      setError(
        errorMessage(
          err,
          'Could not open billing.'
        )
      );

      setBusy(null);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {notice && (
            <p style={{ textAlign: 'center' }}>
              {notice}
            </p>
          )}

          <LoadingSpinner />
        </div>
      </div>
    );
  }

  const state = access?.state;
  const sub = access?.subscription;

  const trialDays =
    catalog.trial?.duration_days ||
    access?.trial_days ||
    30;

  const cardTrial =
    state === 'trial' &&
    sub &&
    sub.platform !== 'trial';

  const carriesOver =
    state === 'trial' &&
    !cardTrial &&
    (access?.days_left || 0) > 2;

  const trialLabel =
    trialDays === 30
      ? '1 month'
      : `${trialDays} days`;

  const trialPlan = cardTrial
    ? catalog.plans.find(
        (p) => p.id === sub.plan_id
      )
    : null;

  const title = {
    new: 'Start your free month',
    trial: 'You’re on your free trial',
    subscribed: 'Your subscription',
    expired: 'Your free trial has ended',
  }[state] || 'Choose your plan';

  const subtitle = {
    new: `Try everything free for ${trialLabel}. Cancel any time before it ends and you won’t be charged.`,

    trial: cardTrial
      ? 'You have full access. Your plan starts automatically when the trial ends.'
      : 'Subscribe any time to keep full access after your trial.',

    subscribed:
      'Thanks for being a member — you have full access.',

    expired:
      'Choose a monthly or yearly plan to keep using the app.',
  }[state] || '';

  const planButton = (plan) => {
    if (!access?.can_purchase) {
      if (sub?.plan_id === plan.id) {
        return {
          label:
            state === 'trial'
              ? 'Starts after your trial'
              : 'Your current plan',
          disabled: true,
        };
      }

      return {
        label:
          state === 'trial'
            ? 'Not available during trial'
            : 'Not available while subscribed',
        disabled: true,
      };
    }

    if (busy === plan.id) {
      return {
        label: 'Redirecting…',
        disabled: true,
      };
    }

    if (access?.trial_available) {
      return {
        label: `Start ${trialLabel} free trial`,
        disabled: !!busy,
        onClick: () =>
          handleStartTrial(plan),
      };
    }

    return {
      label: `Choose ${plan.name}`,
      disabled: !!busy,
      onClick: () =>
        handleChoosePlan(plan),
    };
  };

  return (
    <div className="upgrade-profile-container">
      <div className="container">

        {/* Header */}
        <div className="upgrade-header">
          <h1 className="upgrade-title">
            {title}
          </h1>

          <p className="upgrade-subtitle">
            {subtitle}
          </p>
        </div>

        {/* Notice */}
        {notice && (
          <div style={styles.banner}>
            {notice}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="error-alert">
            {error}

            <button
              onClick={() => setError(null)}
              className="error-close-btn"
            >
              ×
            </button>
          </div>
        )}

        {/* Current subscription */}
        {sub && (
          <div
            className="current-subscription-card"
            style={{ marginBottom: 24 }}
          >
            <div
              className="d-flex flex-wrap align-items-center justify-content-between"
              style={{ gap: 12 }}
            >
              <div>

                <h2 className="current-subscription-title">
                  {state === 'trial'
                    ? 'Free trial'
                    : 'Your current plan'}
                </h2>

                <p className="current-plan-name">
                  {sub.plan_name}
                </p>

                <p className="text-muted1">

                  {state === 'trial' &&
                    `${access.days_left} day${
                      access.days_left === 1
                        ? ''
                        : 's'
                    } left · ends on ${formatDate(
                      sub.expires_at
                    )}`}

                  {cardTrial &&
                    (sub.cancel_at_period_end ||
                    !sub.auto_renew
                      ? ' · cancelled, you won’t be charged'
                      : ` · then ${
                          trialPlan
                            ? `${trialPlan.price_label}/${trialPlan.interval}`
                            : 'your plan'
                        } is charged automatically`)}

                  {state === 'subscribed' &&
                    sub.status === 'past_due' &&
                    'Your last payment failed — please update your card.'}

                  {state === 'subscribed' &&
                    sub.status !== 'past_due' &&
                    (sub.cancel_at_period_end ||
                    !sub.auto_renew
                      ? `Access until ${formatDate(
                          sub.expires_at
                        )} (will not renew)`
                      : `Renews on ${formatDate(
                          sub.expires_at
                        )}`)}
                </p>

                {(state === 'subscribed' ||
                  cardTrial) &&
                  sub.platform === 'ios' && (
                    <p className="text-muted1">
                      Manage this subscription in
                      the App Store on your iPhone.
                    </p>
                  )}

                {(state === 'subscribed' ||
                  cardTrial) &&
                  sub.platform === 'android' && (
                    <p className="text-muted1">
                      Manage this subscription in
                      Google Play on your phone.
                    </p>
                  )}
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                {(state === 'subscribed' ||
                  cardTrial) &&
                  sub.platform === 'web' && (
                    <button
                      style={styles.linkButton}
                      onClick={handleManageBilling}
                      disabled={
                        busy === 'portal'
                      }
                    >
                      {busy === 'portal'
                        ? 'Opening…'
                        : cardTrial
                        ? 'Manage / cancel trial'
                        : 'Manage billing'}
                    </button>
                  )}

                {access.has_access && (
                  <button
                    style={{
                      ...styles.linkButton,
                      background: '#0F4C75',
                      color: 'white',
                    }}
                    onClick={goToApp}
                  >
                    Continue to app
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Free trial offer */}
        {access?.trial_available &&
          catalog.trial && (
            <div style={styles.trialCard}>

              <h2
                style={{
                  margin: 0,
                  fontSize: 26,
                  fontWeight: 800,
                }}
              >
                {trialLabel} free
              </h2>

              {/* FIXED: Render HTML instead of showing <p> tags */}
              <div
                style={{
                  margin: '8px 0 0',
                  opacity: 0.9,
                }}
                dangerouslySetInnerHTML={{
                  __html:
                    catalog.trial.description ||
                    'Full access to every feature.',
                }}
              />

              <p
                style={{
                  margin: '12px 0 0',
                  fontSize: 14,
                  opacity: 0.9,
                }}
              >
                Pick a plan below and add your card
                — you pay $0 today. When the trial
                ends, the plan you picked is charged
                automatically. Cancel any time before
                that and you won’t be charged.
              </p>
            </div>
          )}

        {/* Paid plans */}
        {catalog.plans.length > 0 && (
          <>
            <h2
              style={{
                fontSize: 20,
                fontWeight: 700,
                margin: '0 0 8px',
              }}
            >
              {access?.trial_available
                ? 'Choose the plan to start after your free trial'
                : 'Plans'}
            </h2>

            {carriesOver && (
              <p
                className="text-muted1"
                style={{
                  marginBottom: 16,
                }}
              >
                Your remaining{' '}
                {access.days_left} free days
                carry over — you won’t be charged
                until{' '}
                {formatDate(
                  sub.expires_at
                )}
                .
              </p>
            )}

            <div
              style={{
                ...styles.grid,
                marginTop: 16,
              }}
            >
              {catalog.plans.map((plan) => {
                const btn = planButton(plan);
                const isYearly =
                  plan.plan_type === 'yearly';

                return (
                  <div
                    key={plan.id}
                    style={styles.card(
                      isYearly
                    )}
                  >

                    {/* Savings badge */}
                    {isYearly &&
                      plan.savings_percent > 0 && (
                        <span
                          style={styles.badge}
                        >
                          Save{' '}
                          {
                            plan.savings_percent
                          }%
                        </span>
                      )}

                    {/* Plan name */}
                    <h3
                      style={{
                        margin: 0,
                        fontSize: 18,
                        fontWeight: 700,
                      }}
                    >
                      {plan.name}
                    </h3>

                    {/* Price */}
                    <p style={styles.price}>
                      {plan.price_label}

                      <span
                        style={{
                          fontSize: 16,
                          fontWeight: 500,
                          color: '#6B7280',
                        }}
                      >
                        /{plan.interval}
                      </span>
                    </p>

                    {/* Yearly monthly equivalent */}
                    {isYearly && (
                      <p
                        className="text-muted1"
                        style={{
                          margin:
                            '4px 0 0',
                        }}
                      >
                        Just $
                        {plan.per_month}
                        /month, billed yearly
                      </p>
                    )}

                    {/* Trial pricing */}
                    {access?.trial_available && (
                      <p
                        style={{
                          margin:
                            '8px 0 0',
                          fontWeight: 600,
                          color: '#16A34A',
                        }}
                      >
                        {trialLabel} free,
                        then{' '}
                        {
                          plan.price_label
                        }
                        /
                        {
                          plan.interval
                        }
                      </p>
                    )}

                    {/* FIXED: Render plan description as HTML */}
                    {plan.description && (
                      <div
                        style={{
                          margin:
                            '12px 0 20px',
                          color: '#374151',
                        }}
                        dangerouslySetInnerHTML={{
                          __html:
                            plan.description,
                        }}
                      />
                    )}

                    {/* Plan button */}
                    <button
                      style={styles.button(
                        btn.disabled
                      )}
                      disabled={btn.disabled}
                      onClick={btn.onClick}
                    >
                      {btn.label}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Subscription switching info */}
            {state === 'subscribed' &&
              sub?.platform === 'web' && (
                <p
                  className="text-muted1"
                  style={{
                    marginTop: 16,
                  }}
                >
                  To switch between monthly and
                  yearly, cancel in Manage billing.
                  You keep access until the end of
                  the period, then choose the other
                  plan here.
                </p>
              )}
          </>
        )}

        {/* No plans */}
        {!catalog.trial &&
          catalog.plans.length === 0 && (
            <p className="text-muted1">
              No plans are available right now.
              Please try again later.
            </p>
          )}
      </div>
    </div>
  );
};

export default UpgradeProfile;